import type { CalendarBar, CalendarMilestoneMarker } from '@complan/contracts';
import {
  DEFAULT_STACKING_ROW_CAP,
  assignCalendarLanes,
  barDateRange,
  barGeometryOnTrack,
  canonicalizeColor,
  hexColor,
  higherContrastHexColor,
  salesCycleBarGeometry,
  toBarPixelInterval,
  toCalendarDate,
  toEpochDay,
  type BarGeometry,
  type CalendarTrack,
  type InitiativeStatus,
  type LaneBar,
  type PlainDate,
  type SalesCycleTrack,
} from '@complan/domain';
import type { GridColumn } from './grid-model';

/**
 * The pure bar/milestone layout layer of the Calendar_View (task 22.3).
 *
 * Where {@link import('./grid-model').buildCalendarGrid} builds the orientation-
 * agnostic *scaffolding* — the axis, the Sales Cycle band, the empty lanes and
 * the current-period indicators — this module fills the per-lane track seam it
 * left. It is the layer that finally computes pixel geometry, which the grid
 * model deliberately did not: bars are positioned and clipped through the domain
 * geometry functions ({@link barGeometryOnTrack}, {@link salesCycleBarGeometry}),
 * stacked into non-overlapping sub-rows through {@link assignCalendarLanes}, and
 * the overflow the cap produces is reported rather than dropped (R10.11, R10.15).
 *
 * Everything here is pure: no DOM, no clock, no network. The column width the
 * geometry is scaled against arrives from the caller (the rendered lane measures
 * it), exactly as the domain expects, so the same layout serves any viewport and
 * is trivially testable. The web layer owns no calendar or geometry maths of its
 * own; it only maps the domain's column-unit and pixel outputs onto the CSS the
 * grid renders.
 *
 * ## Non-colour encoding (R10.5, R10.6, R18.8)
 *
 * Every rendered bar carries redundant, colour-independent signals so that the
 * Platform fill is never the sole carrier of meaning: a status *word* rendered on
 * the bar itself, a status *texture* reinforcing it ordinally, the rest of the
 * status *treatment*, and boolean flags for the confidential and
 * elevated-priority markers the bar renders as real icons. Those are computed
 * here as data so the component stays a thin projection and the encoding is unit
 * tested without a DOM.
 */

/** UI spec §4.2 / §4.3: each stacking sub-row is 30px tall — a 26px bar centred in it leaves a 4px gap between stacked bars. */
export const SUB_ROW_HEIGHT_PX = 30;

/** UI spec §4.2: the initiative bar itself is 26px tall. */
export const BAR_HEIGHT_PX = 26;

/** UI spec §4.3: `lane height = max(1, sub-rows) × 30 + 12`px (adjusted for consistent spacing). */
export const LANE_VERTICAL_PADDING_PX = 12;

/** UI spec §4.4: milestone marker vertical offset within a sub-row is `sub-row × 26 + 24`. */
export const MILESTONE_ROW_OFFSET_PX = 24;

/** UI spec §4.4: the milestone marker is a 9px rotated square. */
export const MILESTONE_MARKER_SIZE_PX = 9;

/**
 * The fixed height of the per-Platform milestone swimlane strip.
 *
 * With clustering, milestones at the same date are shown as a single marker with
 * a count badge, so the swimlane height is always minimal (22px) regardless of
 * overlap depth. This saves significant vertical space compared to vertical stacking.
 */
export const MILESTONE_SWIMLANE_HEIGHT_PX = 22;

/** The diamond's vertical centre within the swimlane strip. */
export const MILESTONE_SWIMLANE_MARKER_TOP_PX = 6;

/**
 * The status treatment, one per Initiative status (R9.1).
 *
 * ## This resolves UI spec open decision 1
 *
 * §4.2's corrected table folded Draft, Submitted and Planned onto a single
 * `faded` treatment and flagged the consequence as an open decision: *"Either
 * accept that three statuses share one visual, or introduce a distinct treatment —
 * but the legend text must then be corrected to match."* Three of the six
 * statuses being mutually indistinguishable is a real gap on a planning board
 * whose whole job is showing where each Initiative stands, so the second option is
 * taken: every status now has its own treatment, and the legend enumerates all
 * six.
 *
 * ## Why the treatments are words and textures, not hues
 *
 * The bar's fill is the Platform colour (§4.2, R16.1) and cannot also carry the
 * status, so each status is separated by properties that survive greyscale.
 *
 * The six-glyph set this replaced had two faults. Icons encode *categories*, but
 * status is a **sequence** — draft → submitted → planned → approved → final — so a
 * categorical encoding threw the ordering away and forced the reader to decode each
 * bar in isolation. And six symbols is past the number a reader distinguishes
 * pre-attentively at 11px, which made the legend a codebook rather than a
 * reminder; the `Check`/`CheckCheck` pair carried the set's most important
 * distinction on its weakest visual difference.
 *
 * Two channels replace it, one exact and one coarse:
 *
 * | Status | Word | Fill | Opacity | Inset | Title |
 * |---|---|---|---|---|---|
 * | Draft | `DRAFT` | pale tint + dashed outline | faded | — | — |
 * | Submitted | `SUBMITTED` | pale tint, plain | faded | — | — |
 * | Planned | `PLANNED` | pale tint + very soft hatch | faded | — | — |
 * | Approved | `APPROVED` | solid | full | — | — |
 * | Final | `FINAL` | solid | full | white inner border | — |
 * | Cancelled | `CANCELLED` | solid grey | full | — | struck through |
 *
 * **The word is the primary carrier** and needs no legend at all. It degrades by
 * measured bar width rather than disappearing — see {@link statusLabelTier} — so a
 * 24px bar still shows a letter the reader has already learnt from the wider bars
 * in the same lane.
 *
 * **The fill is the coarse reinforcement**, following the convention MS Project and
 * Primavera established: provisional work is drawn hollow, committed work is solid.
 * It answers only *is this agreed yet?* — pale versus solid — which is the one
 * distinction the eye can make across a whole board without reading. Separating the
 * three pale statuses from each other is the word's job, which is why their marks are
 * deliberately quiet.
 *
 * Status is still not given a hue of its own: the bar has one fill and it belongs
 * to the Platform (R16.1). Colour therefore carries no status meaning here at all
 * except the Cancelled override, which satisfies R10.6 and R18.8 by construction.
 *
 * Cancelled keeps a categorical treatment (solid grey, struck through, no texture)
 * because it genuinely *is* categorical — it sits outside the sequence rather than
 * at a point along it.
 */
export type StatusTreatment = InitiativeStatus;

/** How a {@link StatusTreatment} paints, as data the component maps to CSS. */
export interface StatusStyle {
  readonly treatment: StatusTreatment;
  /** Bar opacity: {@link FADED_OPACITY} for the pre-approval statuses, `1` after (§4.2). */
  readonly opacity: number;
  /** The inset box-shadow for the Final state, or `null` (§4.2). */
  readonly insetBorder: string | null;
  /** The grey fill override for Cancelled, or `null` — the bar keeps its Platform colour (§4.2). */
  readonly colourOverride: string | null;
  /**
   * The hatch laid over the fill, or `null` for every status but Planned (§4.2).
   *
   * Geometry rather than a baked CSS string, because the stripe is drawn in the
   * Platform colour, which this layer never sees. Both the bar and the legend swatch
   * build their CSS from this one spec through {@link hatchCss}, so they cannot drift
   * apart, and {@link renderedBarFill} derives the mean coverage from the same
   * numbers the gradient uses.
   */
  readonly hatch: HatchSpec | null;
  /**
   * How far the Platform colour is lightened toward white before the bar paints it,
   * `0`–`1`. Non-zero for the three pre-approval statuses, whose shared pale fill is
   * the group signal; `0` for Approved, Final and Cancelled.
   *
   * A lightening rather than a `fill-opacity` so the painted colour stays knowable
   * in JS — see {@link PROVISIONAL_FILL_LIGHTEN_ALPHA}.
   */
  readonly fillLightenAlpha: number;
  /** The bar's `border-style`: `dashed` marks Draft, `solid` everywhere else. */
  readonly borderStyle: 'solid' | 'dashed';
  /** The bar's border width in CSS px — Draft's dashed outline is drawn heavier. */
  readonly borderWidthPx: number;
  /**
   * Whether the border takes the Platform colour rather than the default
   * `rgba(0,0,0,.12)` hairline.
   *
   * All three pale statuses do. On an 18% tint the hairline is effectively invisible,
   * which would leave a pale bar with no edge at all against the white lane — and for
   * Draft the outline *is* the encoding. Resolved in the component, the only layer
   * holding the Platform colour.
   */
  readonly borderUsesPlatformColour: boolean;
  /**
   * The title colour when the fill is overridden, or `null` to keep the default
   * white.
   *
   * Only Cancelled sets it. The bar's title is white against a Platform colour
   * chosen to clear 4.5:1, but the Cancelled override replaces that fill with a
   * light grey where white text lands at roughly 1.7:1 — unreadable, and a
   * contrast failure R18.7 does not exempt for a struck-through label. A dark
   * neutral over the grey reaches about 9:1.
   */
  readonly textColour: string | null;
  /** Whether the title is struck through (Cancelled, §4.2). */
  readonly strikethrough: boolean;
}

/** UI spec §4.2: the Cancelled grey override. */
export const CANCELLED_FILL = '#C3CBD6';

/**
 * ## The fill vocabulary (§4.2)
 *
 * | Status | Fill | Told apart by |
 * |---|---|---|
 * | Draft | pale Platform tint | a **dashed** Platform outline |
 * | Submitted | pale Platform tint | nothing added — the plain pale bar |
 * | Planned | pale Platform tint | a **very soft hatch**, bottom-left to top-right |
 * | Approved | solid Platform colour | — |
 * | Final | solid Platform colour | the white inner ring |
 * | Cancelled | solid grey | the struck-through title |
 *
 * The split that carries the most meaning is **pale versus solid**: the three
 * pre-approval statuses are unfilled, and everything from Approved on is solid. That
 * is the Gantt convention (provisional work is drawn hollow) and it is the one
 * distinction legible across a whole board without reading a thing.
 *
 * Within the pale group the three treatments are deliberately *quiet* — a dashed
 * edge, nothing, a faint hatch. They are not trying to be separable at a distance,
 * because the **status word carries that** (see this module's header). The fill only
 * has to answer *is this agreed yet?*; asking it to also separate Draft from
 * Submitted from Planned is what drove the earlier revisions to textures strong
 * enough to fight the 10.5px title.
 *
 * Two earlier attempts and why they went:
 *
 * - A **hatch ramped through three densities** (one per pre-approval status). Three
 *   densities of one weave are hard to tell apart on a short bar, and the ordering
 *   was already in the word.
 * - A **hatch plus a dot grid** at full strength. Both were legible as patterns but
 *   measurably fought the title; the dot grid is gone entirely and the surviving
 *   hatch is much softer.
 */

/**
 * How far the pre-approval fills are lightened toward white — an 18% Platform tint.
 *
 * Shared by Draft, Submitted and Planned, which is the point: the pale fill is the
 * *group* signal, and the per-status marks sit on top of it.
 *
 * Expressed as a lightening of the Platform colour rather than as an opacity so the
 * bar stays fully opaque and the painted colour is knowable in JS. That matters
 * because the title ink and the status capsule are both chosen by contrast against
 * the fill the bar actually paints, and a `fill-opacity` would leave that colour
 * dependent on whatever sits behind the bar.
 */
export const PROVISIONAL_FILL_LIGHTEN_ALPHA = 0.82;

/** UI spec §4.2: Draft's dashed outline width, in CSS px — heavier so the dashes read. */
export const DRAFT_BORDER_WIDTH_PX = 1.5;

/** A repeating diagonal hatch, as geometry rather than as a baked CSS string. */
export interface HatchSpec {
  /**
   * The gradient's progression angle in degrees. Stripes run *perpendicular* to it,
   * so `135` (progressing toward bottom-right) yields stripes running bottom-left to
   * top-right — the `/` direction §4.2 asks for.
   */
  readonly angleDeg: number;
  /** Stripe width in CSS px. */
  readonly stripePx: number;
  /** Stripe-to-stripe period in CSS px. */
  readonly periodPx: number;
  /** Stripe alpha over the bar's fill, `0`–`1`. */
  readonly alpha: number;
}

/**
 * UI spec §4.2: the very soft hatch marking a Planned bar, running bottom-left to
 * top-right.
 *
 * Held as geometry, not as a CSS string, because the stripe is drawn in the **Platform
 * colour** — so the CSS cannot be built until the Platform colour is known, and the
 * mean coverage has to be derived from the same numbers the gradient uses.
 *
 * `alpha` is the softness dial and it is set from measurement. The stripe-to-gap
 * contrast ratio (the banding amplitude the title has to compete with) is what makes
 * a hatch feel harsh, and across the seeded palette this lands at about **1.26:1** —
 * present as a weave, far too shallow to compete with a glyph edge. The earlier
 * full-strength hatch sat near 1.72:1. Title contrast is not the binding constraint
 * here at all: on the pale tint the title clears 10:1 at every alpha tried, so
 * softness could be chosen purely for legibility.
 */
export const PLANNED_HATCH: HatchSpec = {
  angleDeg: 135,
  stripePx: 1.5,
  periodPx: 5,
  alpha: 0.18,
};

/**
 * The CSS `background-image` for a hatch drawn in `inkColour`.
 *
 * A `repeating-linear-gradient` rather than an SVG pattern: it needs no extra DOM
 * node inside the bar, scales with the bar's own box, carries its own period in the
 * colour stops (so no `background-size` is required), and composites over the
 * `background-color` the same element already has.
 */
export function hatchCss(spec: HatchSpec, inkColour: string): string {
  const ink = withAlpha(inkColour, spec.alpha);
  return `repeating-linear-gradient(${String(spec.angleDeg)}deg, ${ink} 0 ${String(spec.stripePx)}px, transparent ${String(spec.stripePx)}px ${String(spec.periodPx)}px)`;
}

/** Mean ink coverage of a hatch — stripe width over period, scaled by stripe alpha. */
export function hatchMeanAlpha(spec: HatchSpec): number {
  return (spec.stripePx / spec.periodPx) * spec.alpha;
}

/** `#rrggbb` plus an alpha, as the `rgba()` a CSS gradient stop needs. */
function withAlpha(hex: string, alpha: number): string {
  const canonical = canonicalizeColor(hex);
  if (canonical === undefined) return hex;
  const channel = (offset: number): number =>
    Number.parseInt(canonical.slice(offset, offset + 2), 16);
  return `rgba(${String(channel(1))},${String(channel(3))},${String(channel(5))},${String(alpha)})`;
}

/**
 * The two candidate bar-title colours, in the lowercase `#rrggbb` spelling the
 * contrast helpers require: white, and the primary ink `--txt`.
 */
const TITLE_LIGHT = hexColor('#ffffff');
const TITLE_DARK = hexColor('#1b2733');

/** The Cancelled title colour; white on {@link CANCELLED_FILL} fails contrast (R18.7). */
export const CANCELLED_TEXT: string = TITLE_DARK;

/**
 * The title colour for a bar painted `fill`: whichever of white and the dark ink
 * reads better against it (R18.7, R18.15).
 *
 * A fixed white title was safe only while Platform colours were themselves
 * validated for contrast, and they no longer are — `platform-color.ts` records
 * that R16.12's 4.5:1 rule was dropped by product decision, so a pale fill now
 * reaches the calendar and takes white text below the body-text minimum with it.
 * Choosing per fill restores the guarantee for any colour an administrator saves,
 * in both directions: white still wins on the whole seeded palette (8.4:1 on
 * `#1a5276`, 6.1:1 on `#935116`), and a pale fill flips to the dark ink instead of
 * rendering an unreadable title.
 *
 * This generalises what Cancelled already did by hand: its grey override takes
 * white to about 1.7:1, which is why {@link StatusStyle.textColour} existed at all.
 *
 * An unparseable `fill` keeps white, the previous behaviour — a malformed colour is
 * a master-data problem to report, not a reason for this function to invent one.
 */
export function barTitleColour(fill: string): string {
  const background = canonicalizeColor(fill);
  if (background === undefined) {
    return TITLE_LIGHT;
  }
  return higherContrastHexColor(background, TITLE_LIGHT, TITLE_DARK);
}

/** The opaque fill and ink of a bar's status capsule; see {@link statusCapsuleColours}. */
export interface StatusCapsuleColours {
  /** An opaque `#rrggbb` fill — never a translucent or `color-mix` value. */
  readonly background: string;
  /** The label ink, the opposite pole from {@link StatusCapsuleColours.background}. */
  readonly text: string;
}

/**
 * The status capsule's colours for a bar painting `effectiveFill`: an **opaque**
 * background chosen for maximum contrast against the bar, and the label ink chosen
 * for maximum contrast against that background.
 *
 * The capsule reads as an *inverted chip*. Its background is whichever of white and
 * the dark ink stands furthest from the bar's own fill — the same choice
 * {@link barTitleColour} makes for the title — and the label then takes the other
 * pole. So a navy bar gets a white capsule with dark text, and a pale bar gets a
 * dark capsule with white text, always at roughly 15:1 between the two.
 *
 * ## The two contrast guarantees, which are different rules
 *
 * - **Label against the chip: ≥ 7:1.** Text contrast, so R18.7's 4.5:1 applies and
 *   is comfortably exceeded — the two poles are near-extremes by construction.
 * - **Chip against the bar: ≥ 3:1.** This boundary carries no text, so the
 *   applicable rule is WCAG 1.4.11 non-text contrast at 3:1. Choosing the better of
 *   two fixed poles bottoms out at **3.90:1** on a mid-tone fill (`#818181`, the
 *   luminance that minimises the maximum), so 4.5:1 is not reachable here without
 *   abandoning the two-pole scheme — and is not required for a boundary.
 *
 * Mid-tone fills are a real input rather than a hypothetical: R16.12's 4.5:1 rule on
 * Platform colours was dropped by product decision, so an administrator can save one.
 *
 * ## Why opaque rather than a scrim
 *
 * This replaced a `color-mix(currentColor 22%, transparent)` wash. A translucent
 * scrim inherits whatever is beneath it, so its contrast was a function of the
 * Platform colour an administrator happened to save and of the hatch density
 * underneath — neither of which this module controls, and R16.12's 4.5:1 rule on
 * Platform colours was dropped by product decision (see `platform-color.ts`). An
 * opaque fill makes the capsule's contrast a property of the capsule instead of an
 * accident of its surroundings.
 *
 * It also settles the legibility risk of running the status word and the hatch
 * together: an opaque capsule occludes the texture outright, so the word never sits
 * on stripes at all, rather than merely sitting on a partly-muted version of them.
 *
 * Note that the bar's own `opacity` (`.88` across the pre-approval statuses, §4.2)
 * still composites the capsule along with everything else, so a Draft capsule is
 * 88% opaque *on the page* even though its own background has no alpha. That fade
 * is the status encoding and is deliberately left in place; at `.88` between two
 * near-extreme poles the capsule's internal contrast stays far above the minimum.
 */
export function statusCapsuleColours(effectiveFill: string): StatusCapsuleColours {
  const background = barTitleColour(effectiveFill);
  // The two candidates are opposite poles, so "not the background" *is* the
  // higher-contrast ink and no second comparison is needed.
  return {
    background,
    text: background === TITLE_LIGHT ? TITLE_DARK : TITLE_LIGHT,
  };
}

/**
 * The fill a bar effectively paints once its status texture is composited over the
 * Platform colour — what {@link barTitleColour} must judge contrast against.
 *
 * A hatch of white stripes lightens the fill by its mean coverage, and the title
 * sits on the result, not on the raw Platform colour. Blending that mean in before
 * choosing the ink is what keeps a textured bar inside R18.7: without it, a
 * mid-tone Platform colour plus a dense hatch could take a white title below 4.5:1
 * while the unblended calculation still reported it safe.
 *
 * Flat average rather than per-stripe sampling: the text crosses many stripes at
 * 10.5px, so the mean is the background it reads against. An unparseable `fill`
 * is returned unchanged for {@link barTitleColour} to handle.
 */
export function effectiveBarFill(fill: string, whiteCoverage: number): string {
  return blendHex(fill, '#ffffff', whiteCoverage);
}

/**
 * Composites `target` over `fill` at `alpha`, returning an opaque `#rrggbb`.
 *
 * The one blend every fill computation here goes through, so the flat-average model
 * is stated once: text at 10.5px crosses many texture cells, so the mean coverage is
 * the background it reads against, and sampling per stripe would model a precision
 * the eye does not have. An unparseable `fill` is returned unchanged for
 * {@link barTitleColour} to handle rather than having a colour invented for it.
 */
export function blendHex(fill: string, target: string, alpha: number): string {
  if (alpha <= 0) return fill;
  const clamped = Math.min(1, alpha);
  const background = canonicalizeColor(fill);
  const foreground = canonicalizeColor(target);
  if (background === undefined || foreground === undefined) return fill;

  const channel = (offset: number): number => {
    const base = Number.parseInt(background.slice(offset, offset + 2), 16);
    const over = Number.parseInt(foreground.slice(offset, offset + 2), 16);
    return Math.max(0, Math.min(255, Math.round(base + (over - base) * clamped)));
  };
  const hex = (value: number): string => value.toString(16).padStart(2, '0');
  return `#${hex(channel(1))}${hex(channel(3))}${hex(channel(5))}`;
}

/**
 * The colour a bar finally presents to the eye, given its base fill and status
 * treatment — the background every contrast decision on the bar must be judged
 * against.
 *
 * Three separate things shift a bar's colour, and **they compound**:
 *
 * 1. `fillLightenAlpha` — the pale pre-approval tint, a lightening toward white.
 * 2. the `hatch` — its mean coverage, blended toward the *Platform colour* (the ink
 *    the stripes are drawn in), so on a pale bar the hatch pulls back toward the hue.
 * 3. `1 - opacity` — the `.88` fade on the pre-approval statuses, which composites
 *    the bar over the lane background.
 *
 * Judging contrast against only one of them is how this goes wrong, and it already
 * did once: an earlier revision checked only the texture term and shipped a hatch
 * that, compounded with the `.88` fade, put two seeded Platform colours under the
 * R18.7 minimum. That is the same class of defect as the `.72` opacity value, which
 * an `@axe-core/playwright` scan caught only after the fact.
 *
 * Takes the **Platform colour** rather than an already-resolved fill, because the
 * hatch ink is that colour and the Cancelled override is applied here too — so one
 * argument determines the whole result and no caller can compose the terms wrongly.
 *
 * The opacity term is modelled as a composite over **white** rather than over the
 * real lane background. White is the worst case for the white end of the ink pair —
 * it is the direction that erodes contrast — so the result is a conservative bound
 * rather than an exact prediction, and it needs no layout knowledge to compute.
 */
export function renderedBarFill(platformColour: string, style: StatusStyle): string {
  // Order mirrors the paint order: the override or Platform colour is the base, the
  // tint lightens it into the background colour, the hatch composites over that, and
  // the fade composites the result over the lane.
  const base = style.colourOverride ?? platformColour;
  const tinted = blendHex(base, '#ffffff', style.fillLightenAlpha);
  const hatched =
    style.hatch === null ? tinted : blendHex(tinted, platformColour, hatchMeanAlpha(style.hatch));
  return blendHex(hatched, '#ffffff', 1 - style.opacity);
}

/** UI spec §4.2: the Final inset white border. */
export const FINAL_INSET_BORDER = 'inset 0 0 0 2px rgba(255,255,255,.75)';

/**
 * The faded opacity shared by Draft, Submitted and Planned.
 *
 * §4.2's dummy value was `.72`. Raised to `.88`: at `.72`, the bar's white title
 * text composited over a *faded* fill drops below the 4.5:1 body-text minimum
 * (R18.7/R18.15) for the darker end of the seeded Platform palette (migration
 * 0008's `#935116`/`#196f3d`, chosen specifically to clear 4.5:1 against the
 * Calendar_View background at full strength, per that migration's own comment)
 * — confirmed by an `@axe-core/playwright` scan (task 26.1) that failed on this
 * exact element. `.88` keeps a visibly reduced, translucent look (still `< 1`,
 * still distinct from the Approved/Final full-opacity treatments the same
 * property test checks) while every seeded Platform colour's white title text
 * reaches at least 4.6:1 once faded.
 */
export const FADED_OPACITY = 0.88;

/**
 * Resolves a bar's status to its treatment — one per status, per this module's
 * note on open decision 1.
 *
 * Read as a table rather than a chain of special cases, because the point is that
 * all six are peers now: the record is a total map over the status union, so a
 * status added to R9.1's lifecycle is a compile error here rather than a bar that
 * silently falls back to someone else's treatment.
 *
 * Translucency marks the pre-approval half of the lifecycle (Draft, Submitted,
 * Planned): those are proposals, and reading a board at a glance should separate
 * what is agreed from what is not. Everything from Approved on is opaque.
 */
const STATUS_STYLES: Record<InitiativeStatus, StatusStyle> = {
  draft: {
    // Pale, and the only status with a dashed outline.
    treatment: 'draft',
    opacity: FADED_OPACITY,
    insetBorder: null,
    colourOverride: null,
    hatch: null,
    fillLightenAlpha: PROVISIONAL_FILL_LIGHTEN_ALPHA,
    borderStyle: 'dashed',
    borderWidthPx: DRAFT_BORDER_WIDTH_PX,
    borderUsesPlatformColour: true,
    textColour: null,
    strikethrough: false,
  },
  submitted: {
    // The plain pale bar: same fill as Draft, nothing added. Being the *unmarked*
    // member of the pale group is itself the distinction.
    treatment: 'submitted',
    opacity: FADED_OPACITY,
    insetBorder: null,
    colourOverride: null,
    hatch: null,
    fillLightenAlpha: PROVISIONAL_FILL_LIGHTEN_ALPHA,
    borderStyle: 'solid',
    borderWidthPx: 1,
    borderUsesPlatformColour: true,
    textColour: null,
    strikethrough: false,
  },
  planned: {
    // Pale, plus the one surviving texture: a very soft bottom-left-to-top-right
    // hatch in the Platform colour.
    treatment: 'planned',
    opacity: FADED_OPACITY,
    insetBorder: null,
    colourOverride: null,
    hatch: PLANNED_HATCH,
    fillLightenAlpha: PROVISIONAL_FILL_LIGHTEN_ALPHA,
    borderStyle: 'solid',
    borderWidthPx: 1,
    borderUsesPlatformColour: true,
    textColour: null,
    strikethrough: false,
  },
  approved: {
    // From here on the bar is solid: the Platform colour at full strength.
    treatment: 'approved',
    opacity: 1,
    insetBorder: null,
    colourOverride: null,
    hatch: null,
    fillLightenAlpha: 0,
    borderStyle: 'solid',
    borderWidthPx: 1,
    borderUsesPlatformColour: false,
    textColour: null,
    strikethrough: false,
  },
  final: {
    treatment: 'final',
    opacity: 1,
    insetBorder: FINAL_INSET_BORDER,
    colourOverride: null,
    hatch: null,
    fillLightenAlpha: 0,
    borderStyle: 'solid',
    borderWidthPx: 1,
    borderUsesPlatformColour: false,
    textColour: null,
    strikethrough: false,
  },
  cancelled: {
    // Solid grey and struck through. No hatch: Cancelled sits outside the lifecycle
    // the fill vocabulary describes, so a texture would place it on a scale it is
    // not part of.
    treatment: 'cancelled',
    opacity: 1,
    insetBorder: null,
    colourOverride: CANCELLED_FILL,
    hatch: null,
    fillLightenAlpha: 0,
    borderStyle: 'solid',
    borderWidthPx: 1,
    borderUsesPlatformColour: false,
    textColour: CANCELLED_TEXT,
    strikethrough: true,
  },
};

/** The treatment for one status; see {@link STATUS_STYLES}. */
export function resolveStatusStyle(status: InitiativeStatus): StatusStyle {
  return STATUS_STYLES[status];
}

/**
 * i18n keys for the short, colour-independent status words rendered on the bar
 * and in tooltips (R10.6, R18.9). The text lives in the externalized catalogue
 * under `status.*`; this module carries only the keys, so no user-facing status
 * word is inlined in source. The component resolves each key with `t`.
 */
export const STATUS_WORD_KEYS: Record<InitiativeStatus, string> = {
  draft: 'status.draft',
  submitted: 'status.submitted',
  planned: 'status.planned',
  approved: 'status.approved',
  final: 'status.final',
  cancelled: 'status.cancelled',
};

/**
 * Three-letter status abbreviations for bars too narrow for the full word.
 *
 * Externalized like the full words, and for a stronger reason: an abbreviation is
 * not derivable from a translated word by truncation. German "Eingereicht" does not
 * abbreviate to "SUB", and cutting a word at three characters produces different
 * collisions in every language. A translator therefore has to choose these, which
 * is exactly why they are catalogue entries rather than `slice(0, 3)`.
 */
export const STATUS_SHORT_KEYS: Record<InitiativeStatus, string> = {
  draft: 'statusShort.draft',
  submitted: 'statusShort.submitted',
  planned: 'statusShort.planned',
  approved: 'statusShort.approved',
  final: 'statusShort.final',
  cancelled: 'statusShort.cancelled',
};

/**
 * Single-letter status marks, the last legible step before a bar can say nothing.
 *
 * Distinctness is a per-language obligation these keys cannot enforce: the six
 * English letters `D S P A F C` are unique, but a translator must keep their own
 * six unique too. `statusLetterUniqueness` in the unit tests pins it for the
 * shipped catalogue.
 */
export const STATUS_LETTER_KEYS: Record<InitiativeStatus, string> = {
  draft: 'statusLetter.draft',
  submitted: 'statusLetter.submitted',
  planned: 'statusLetter.planned',
  approved: 'statusLetter.approved',
  final: 'statusLetter.final',
  cancelled: 'statusLetter.cancelled',
};

/**
 * How much of the status word a bar has room for.
 *
 * `full` spells it, `short` gives three letters, `letter` gives one. The tier is
 * resolved here in the pure layer rather than by a CSS container query because the
 * layer is the only place that knows the *measured* pixel width — the same
 * measurement lane assignment already runs on — and because it keeps the choice
 * testable without a DOM.
 */
export type StatusLabelTier = 'full' | 'short' | 'letter';

/**
 * Least bar width, in CSS px, that fits the spelled-out status *and* leaves the
 * title a readable share.
 *
 * The longest English words (`SUBMITTED`, `CANCELLED`) need about 66px of capsule
 * at 9px uppercase. 150px leaves roughly 70px of title after the capsule, the gap
 * and the bar's own padding — around nine characters, enough for a title to be
 * more than an ellipsis. Below this the word wins an argument it should lose: the
 * title is what identifies the Initiative.
 */
export const STATUS_LABEL_FULL_MIN_WIDTH_PX = 150;

/**
 * Least bar width that fits the three-letter abbreviation with some title left.
 *
 * The capsule costs about 32px here, so 64px keeps a little over 20px of title —
 * short, but a recognisable prefix rather than nothing.
 */
export const STATUS_LABEL_SHORT_MIN_WIDTH_PX = 64;

/**
 * The widest label tier that fits a bar of `widthPx`.
 *
 * Thresholds are on the bar's rendered width including the R10.4 minimum widening,
 * because that is the box the label actually has to fit inside.
 */
export function statusLabelTier(widthPx: number): StatusLabelTier {
  if (widthPx >= STATUS_LABEL_FULL_MIN_WIDTH_PX) return 'full';
  if (widthPx >= STATUS_LABEL_SHORT_MIN_WIDTH_PX) return 'short';
  return 'letter';
}

/** The i18n key for a status at a given label tier (§4.2). */
export function statusLabelKey(status: InitiativeStatus, tier: StatusLabelTier): string {
  switch (tier) {
    case 'full':
      return STATUS_WORD_KEYS[status];
    case 'short':
      return STATUS_SHORT_KEYS[status];
    case 'letter':
      return STATUS_LETTER_KEYS[status];
  }
}

/** A milestone marker placed at a horizontal position within a lane sub-row (§4.4). */
export interface PlacedMilestone {
  readonly milestoneTypeId: string;
  /** The type's display name, resolved server-side; `null` only for a stale payload. */
  readonly milestoneTypeName: string | null;
  readonly plannedDate: string;
  readonly completed: boolean;
  /** Horizontal centre as a percentage of the track width, `0`–`100`. */
  readonly leftPercent: number;
}

/** One bar placed in a lane, with everything the renderer needs (geometry + encoding). */
export interface PlacedBar {
  readonly bar: CalendarBar;
  /** 0-based sub-row within the lane; the vertical offset is `lane × 30`. */
  readonly lane: number;
  /** Left edge as a percentage of the track width, `0`–`100`. */
  readonly leftPercent: number;
  /** Width as a percentage of the track width. */
  readonly widthPercent: number;
  /** The domain geometry, carried for the continuation markers and stored dates. */
  readonly geometry: BarGeometry;
  /** The §4.2 status treatment (colour-independent). */
  readonly statusStyle: StatusStyle;
  /** The bar's rendered width in CSS px, including the R10.4 minimum widening. */
  readonly widthPx: number;
  /** How much of the status word this bar has room for (§4.2). */
  readonly labelTier: StatusLabelTier;
  /** Whether a `🔒` confidential icon prefixes the bar (R10.6). */
  readonly confidential: boolean;
  /** Milestones falling inside the rendered track, positioned within the lane. */
  readonly milestones: readonly PlacedMilestone[];
}

/**
 * One milestone as it appears in the Platform swimlane strip at the top of the
 * lane, carrying everything the strip and its connector need.
 *
 * Unlike {@link PlacedMilestone} (which is anchored to its bar's sub-row), a
 * swimlane milestone lives in the shared strip above every bar, so it must carry
 * a reference back to the Initiative it belongs to: `parentBarId` for the shared
 * hover/focus highlight, and `parentLeftPercent`/`parentTopPx` so the connector
 * can draw from the diamond down to the exact bar it wires to.
 */
export interface SwimlaneMilestone {
  /** The parent Initiative's bar id — the highlight and Detail_Panel target. */
  readonly parentBarId: string;
  readonly parentInitiativeRef: string;
  readonly parentTitle: string;
  readonly milestoneTypeId: string;
  /** The type's display name, resolved server-side; `null` only for a stale payload. */
  readonly milestoneTypeName: string | null;
  /**
   * Index of this milestone within its parent bar's milestone list — the same
   * index {@link laneFocusItems} assigns the inline marker, so the swimlane can
   * look up the roving-tabindex props for its `(lane, item)` focus coordinate
   * (task 22.4) now that the swimlane marker is the milestone's only rendering.
   */
  readonly milestoneIndex: number;
  // `string`, matching `PlacedMilestone.plannedDate` (this is built from it in
  // `buildSwimlane`, and only ever reaches `formatDate`, whose `DateInput`
  // already accepts a plain string) - not the branded `PlainDate` this
  // previously declared, which no assignment here could satisfy without
  // re-validating a value {@link CalendarMilestoneMarker}'s wire schema already
  // branded once.
  readonly plannedDate: string;
  readonly completed: boolean;
  /** Horizontal centre of the milestone date, percent of track width (`0`–`100`). */
  readonly leftPercent: number;
  /** The parent bar's left edge, percent of track width — the connector's foot. */
  readonly parentLeftPercent: number;
  /** The parent bar's width, percent of track width — used to detect edge alignment. */
  readonly parentWidthPercent: number;
  /** The parent bar's `top` within the lane track, in px — where the connector lands. */
  readonly parentTopPx: number;
}

/** The laid-out contents of one Platform lane: placed bars, sub-row count, overflow. */
export interface LaneLayout {
  readonly bars: readonly PlacedBar[];
  /** Sub-rows actually rendered, at least 1 so an empty lane still has a track height. */
  readonly subRowCount: number;
  /** Lane track height in px: swimlane strip (if any) + `max(1, sub-rows) × 26 + 12` (§4.3). */
  readonly heightPx: number;
  /** Initiatives the cap kept off screen for this lane (R10.15). */
  readonly overflowCount: number;
  /** The smallest cap that would render the whole lane — what an expand re-runs with. */
  readonly requiredRowCap: number;
  /**
   * The milestone swimlane markers for this lane, in reading order, or empty when
   * the lane has no calendar-relevant milestone.
   */
  readonly swimlane: readonly SwimlaneMilestone[];
  /**
   * The vertical offset (px) applied to every bar and inline marker so they sit
   * *below* the swimlane strip. `0` when the lane has no swimlane, so lanes
   * without milestones are laid out exactly as before.
   */
  readonly barsOffsetPx: number;
}

/** Options for {@link layoutLane}: the track, the measured column width and the cap. */
export interface LaneLayoutInput {
  /** The day-proportional track (month / calendar-week), or `undefined` in Sales_Cycle. */
  readonly track?: CalendarTrack | undefined;
  /** The Sales_Cycle track, or `undefined` in the day-proportional orientations. */
  readonly salesCycleTrack?: SalesCycleTrack | undefined;
  /** The axis columns, used to place milestones by date. */
  readonly columns: readonly GridColumn[];
  /** Measured rendered width of one track column, in CSS pixels. */
  readonly columnWidthPx: number;
  /** Rows to render per lane before overflowing; defaults to the domain default. */
  readonly rowCap?: number | undefined;
}

/**
 * Lays out one Platform lane's bars into non-overlapping sub-rows (R10.11), caps
 * the rendered rows and reports the overflow (R10.15), and positions each bar and
 * its milestones as track-relative percentages.
 *
 * Geometry and lane assignment are the domain's; this function only chooses which
 * geometry function the orientation calls for, maps the domain's column-unit and
 * pixel outputs onto percentages, and attaches the non-colour encoding. A bar
 * whose stored range lies entirely outside the track yields no geometry and is
 * simply absent (R10.16): no placeholder, no reserved slot.
 */
export function layoutLane(bars: readonly CalendarBar[], input: LaneLayoutInput): LaneLayout {
  const rowCap = input.rowCap ?? DEFAULT_STACKING_ROW_CAP;
  const columnCount = Math.max(input.columns.length, 1);

  // 1. Geometry per bar, dropping bars entirely outside the track (R10.16).
  const withGeometry: { bar: CalendarBar; geometry: BarGeometry }[] = [];
  for (const bar of bars) {
    const geometry = geometryFor(bar, input);
    if (geometry) withGeometry.push({ bar, geometry });
  }

  // 2. Lane assignment on the rendered pixel intervals (R10.11), which is what the
  //    domain requires so the minimum-width widening cannot produce visual overlap.
  const laneBars: LaneBar[] = withGeometry.map(({ bar, geometry }) => ({ id: bar.id, geometry }));
  const assignment = assignCalendarLanes(laneBars, {
    columnWidthPx: input.columnWidthPx,
    rowCap,
  });

  const geometryById = new Map(withGeometry.map((entry) => [entry.bar.id, entry]));
  const placed: PlacedBar[] = [];
  for (const assigned of assignment.placed) {
    const entry = geometryById.get(assigned.id);
    if (!entry) continue;
    const { bar, geometry } = entry;
    // The same pixel interval lane assignment ran on, so the label tier is chosen
    // against the width the bar is actually drawn at — minimum widening included.
    const { widthPx } = toBarPixelInterval(geometry, input.columnWidthPx);
    placed.push({
      bar,
      lane: assigned.lane,
      leftPercent: (geometry.startColumns / columnCount) * 100,
      widthPercent: (geometry.widthColumns / columnCount) * 100,
      geometry,
      statusStyle: resolveStatusStyle(bar.status),
      widthPx,
      labelTier: statusLabelTier(widthPx),
      confidential: bar.isConfidential,
      milestones: placeMilestones(bar.milestones, input, columnCount),
    });
  }

  const subRowCount = Math.max(assignment.laneCount, 1);

  // The swimlane is derived from the bars actually placed, so a milestone whose
  // parent bar overflowed the cap (R10.15) or fell off-track (R10.16) is absent
  // from the strip too — the strip can never point at a bar that is not drawn.
  const { swimlane, heightPx: swimlaneHeightPx } = buildSwimlane(placed);
  const barsOffsetPx = swimlaneHeightPx;

  return {
    bars: placed,
    subRowCount,
    heightPx: barsOffsetPx + subRowCount * SUB_ROW_HEIGHT_PX + LANE_VERTICAL_PADDING_PX,
    overflowCount: assignment.unrenderedCount,
    requiredRowCap: assignment.requiredRowCap,
    swimlane,
    barsOffsetPx,
  };
}

/** The `top` of a bar's sub-row within the lane track, before the swimlane offset. */
export function barTopPx(lane: number): number {
  return lane * SUB_ROW_HEIGHT_PX + (SUB_ROW_HEIGHT_PX - BAR_HEIGHT_PX) / 2;
}

/**
 * Lifts every placed bar's milestones into the shared swimlane strip, each
 * carrying a reference back to its parent bar so the strip can wire the diamond
 * to the Initiative it belongs to (a connector down to the bar, and a shared
 * hover/focus highlight).
 *
 * Ordered by horizontal position then parent id so the strip is deterministic
 * (R18.14) and reads left to right the same way the lane's bars and inline
 * markers do.
 */
/**
 * Build the per-lane milestone swimlane strip from the placed bars.
 *
 * With clustering, all overlapping milestones are shown in a single marker with
 * a count badge, so the height is always fixed at 22px regardless of overlap depth.
 */
function buildSwimlane(placed: readonly PlacedBar[]): {
  swimlane: readonly SwimlaneMilestone[];
  heightPx: number;
} {
  const swimlane: SwimlaneMilestone[] = [];
  for (const bar of placed) {
    // `milestoneIndex` is the index within this bar's own milestone list, exactly
    // as {@link laneFocusItems} assigns it, so the two never drift.
    bar.milestones.forEach((milestone, milestoneIndex) => {
      swimlane.push({
        parentBarId: bar.bar.id,
        parentInitiativeRef: bar.bar.initiativeRef,
        parentTitle: bar.bar.title,
        milestoneTypeId: milestone.milestoneTypeId,
        milestoneTypeName: milestone.milestoneTypeName,
        milestoneIndex,
        plannedDate: milestone.plannedDate,
        completed: milestone.completed,
        leftPercent: milestone.leftPercent,
        parentLeftPercent: bar.leftPercent,
        parentWidthPercent: bar.widthPercent,
        parentTopPx: barTopPx(bar.lane),
      });
    });
  }
  const sortedSwimlane = swimlane.sort((a, b) => {
    if (a.leftPercent !== b.leftPercent) return a.leftPercent - b.leftPercent;
    return a.parentBarId < b.parentBarId ? -1 : a.parentBarId > b.parentBarId ? 1 : 0;
  });

  // With clustering, height is always fixed
  const heightPx = sortedSwimlane.length === 0 ? 0 : MILESTONE_SWIMLANE_HEIGHT_PX;

  return { swimlane: sortedSwimlane, heightPx };
}

/** The rendered-track geometry for one bar in the current orientation, or `undefined`. */
function geometryFor(bar: CalendarBar, input: LaneLayoutInput): BarGeometry | undefined {
  const range = barDateRange(toCalendarDate(bar.startDate), toCalendarDate(bar.endDate));
  if (input.salesCycleTrack) {
    return salesCycleBarGeometry(input.salesCycleTrack, bar.salesCycleIds, range);
  }
  if (input.track) {
    return barGeometryOnTrack(input.track, range);
  }
  return undefined;
}

/**
 * Positions a bar's calendar-relevant milestones within the rendered track (§4.4).
 *
 * A milestone is centred on the day it falls, found by locating the axis column
 * whose date range contains it and interpolating across that column. Milestones
 * outside the rendered track (a milestone in a clipped tail, say) are dropped, so
 * no marker is drawn outside the year (R10.14, R10.16).
 */
function placeMilestones(
  milestones: readonly CalendarMilestoneMarker[],
  input: LaneLayoutInput,
  columnCount: number,
): readonly PlacedMilestone[] {
  const placed: PlacedMilestone[] = [];
  for (const milestone of milestones) {
    const leftPercent = milestoneLeftPercent(milestone.plannedDate, input, columnCount);
    if (leftPercent === null) continue;
    placed.push({
      milestoneTypeId: milestone.milestoneTypeId,
      milestoneTypeName: milestone.milestoneTypeName,
      plannedDate: milestone.plannedDate,
      completed: milestone.completed,
      leftPercent,
    });
  }
  return placed;
}

/** The horizontal centre of a milestone date as a percent of track width, or `null` if off-track. */
function milestoneLeftPercent(
  plannedDate: PlainDate,
  input: LaneLayoutInput,
  columnCount: number,
): number | null {
  const target = toCalendarDate(plannedDate);
  const targetEpoch = toEpochDay(target);

  const column = input.columns.find(
    (col) => toEpochDay(col.start) <= targetEpoch && toEpochDay(col.end) >= targetEpoch,
  );
  if (!column) return null;

  // Day-proportional orientations interpolate across the column by day; the
  // Sales_Cycle orientation has no day track, so the marker centres on the column.
  if (input.track) {
    const startEpoch = toEpochDay(column.start);
    const endEpoch = toEpochDay(column.end);
    const span = endEpoch - startEpoch + 1;
    const dayOffset = targetEpoch - startEpoch;
    const withinColumn = span <= 0 ? 0.5 : (dayOffset + 0.5) / span;
    return ((column.index + withinColumn) / columnCount) * 100;
  }
  return ((column.index + 0.5) / columnCount) * 100;
}

/**
 * The overflow indicator's state for one grouping (R10.15).
 *
 * `unrenderedCount` is what the indicator states; the expand action re-runs the
 * layout with `requiredRowCap` and **changes no applied filter** — the cap is a
 * rendering budget, not a filter (R10.15).
 */
export interface OverflowState {
  readonly unrenderedCount: number;
  readonly requiredRowCap: number;
  readonly expanded: boolean;
}

/**
 * One focusable item of a lane: either an Initiative bar or one of its milestone
 * markers. The keyboard composite widget (task 22.4) walks these left to right,
 * so a bar and its markers are distinct, individually reachable stops (R10.11's
 * "each Initiative bar remains individually selectable by keyboard", and the
 * markers alongside it).
 */
export type LaneFocusItem =
  | { readonly kind: 'bar'; readonly placed: PlacedBar }
  | {
      readonly kind: 'milestone';
      readonly placed: PlacedBar;
      readonly milestone: PlacedMilestone;
      /** Index of the marker within its bar's milestone list, for a stable key. */
      readonly milestoneIndex: number;
    };

/**
 * Flattens a laid-out lane into the left-to-right order the keyboard model walks
 * (task 22.4).
 *
 * A lane's focusable items are its bars and every bar's milestone markers,
 * ordered by horizontal position (`leftPercent`) so the arrow keys move through
 * them in reading order regardless of which sub-row they stack into. Ties break
 * by bar id then marker index so the order is deterministic (R18.14) and matches
 * whatever order the grid renders in. Both the grid's DOM order and the focus
 * grid's per-lane item count are derived from this one function, so the roving
 * `(lane, item)` coordinates can never drift from what is on screen.
 */
export function laneFocusItems(layout: LaneLayout): readonly LaneFocusItem[] {
  const items: LaneFocusItem[] = [];
  for (const placed of layout.bars) {
    items.push({ kind: 'bar', placed });
    placed.milestones.forEach((milestone, milestoneIndex) => {
      items.push({ kind: 'milestone', placed, milestone, milestoneIndex });
    });
  }
  return items.sort((a, b) => {
    const leftA = focusItemLeftPercent(a);
    const leftB = focusItemLeftPercent(b);
    if (leftA !== leftB) return leftA - leftB;
    const idA = a.placed.bar.id;
    const idB = b.placed.bar.id;
    if (idA !== idB) return idA < idB ? -1 : 1;
    // Same bar: the bar itself precedes its markers, then markers by index.
    const orderA = a.kind === 'bar' ? -1 : a.milestoneIndex;
    const orderB = b.kind === 'bar' ? -1 : b.milestoneIndex;
    return orderA - orderB;
  });
}

function focusItemLeftPercent(item: LaneFocusItem): number {
  return item.kind === 'bar' ? item.placed.leftPercent : item.milestone.leftPercent;
}
